import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@jaylabs/ui';
import Logo from '../components/Logo';
import UserBadgeBar from '../components/UserBadgeBar';
import { API_URL } from '../utils/apiUrl';
import './Home.css';

function Home() {
  const { isAuthenticated } = useAuth();
  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';

  return (
    <div
      className="dd-home-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      {isAuthenticated && (
        <div className="dd-hstack" style={{ justifyContent: 'flex-end', marginBottom: 'var(--dd-space-4)' }}>
          <UserBadgeBar />
        </div>
      )}

      <div className="dd-container">
        <div className="dd-home-grid">
          {/* LEFT COLUMN */}
          <div>
            {/* Header */}
            <div className="dd-vstack dd-gap-2 dd-mb-10" style={{ alignItems: 'flex-start' }}>
              <Logo size="lg" clickable={false} />
              <h2 className="dd-home-subtitle">Turn Your Tasks Into Achievements</h2>
              <p className="dd-home-tagline">
                Gamified task management for ADHD &amp; neurodivergent brains
              </p>
            </div>

            {/* Buttons */}
            <div className="dd-hstack dd-gap-4 dd-mb-12">
              {isAuthenticated ? (
                <RouterLink to="/dashboard">
                  <Button variant="success" size="lg">Go to Dashboard</Button>
                </RouterLink>
              ) : (
                <>
                  <RouterLink to="/register">
                    <Button variant="success" size="lg">Get Started</Button>
                  </RouterLink>
                  <RouterLink to="/login">
                    <Button variant="primary" size="lg">Login</Button>
                  </RouterLink>
                </>
              )}
            </div>

            {/* Features Card */}
            <div className="dd-home-features">
              <h3>Features:</h3>
              <div className="dd-vstack dd-gap-4">
                <p className="dd-home-feature-item">🎯 Create custom tasks with point values</p>
                <p className="dd-home-feature-item">🔥 Build streaks with emergency protection</p>
                <p className="dd-home-feature-item">🏆 Compete on public leaderboards</p>
                <p className="dd-home-feature-item">👥 Challenge friends directly</p>
                <p className="dd-home-feature-item">😊 Random acts of self-kindness</p>
                <p className="dd-home-feature-item">⚡ Energy level tracking</p>
                <p className="dd-home-feature-item">🧩 Task breakdown assistance</p>
                <p className="dd-home-feature-item">🙌 Virtual body doubling sessions</p>
                <p className="dd-home-feature-item">🔔 Hyperfocus warnings</p>
                <p className="dd-home-feature-item">🎚️ Customizable difficulty ratings</p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            <h3 className="dd-heading-lg dd-mb-5">System Status:</h3>
            <SystemStatus />
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemStatus() {
  const [apiStatus, setApiStatus] = React.useState('checking');
  const [dbStatus, setDbStatus] = React.useState('checking');

  React.useEffect(() => {
    fetch(`${API_URL}/api/health`)
      .then(res => res.json())
      .then(() => setApiStatus('connected'))
      .catch(() => setApiStatus('error'));

    fetch(`${API_URL}/api/db-test`)
      .then(res => res.json())
      .then(() => setDbStatus('connected'))
      .catch(() => setDbStatus('error'));
  }, []);

  return (
    <div className="dd-vstack dd-gap-4">
      <div className={`dd-status-card ${apiStatus === 'connected' ? 'dd-status-card-ok' : 'dd-status-card-err'}`}>
        <div className="dd-vstack dd-gap-2" style={{ alignItems: 'flex-start' }}>
          <div className="dd-hstack dd-gap-2">
            <span className="dd-text-semibold">API Server:</span>
            <span className={`dd-status-badge ${apiStatus === 'connected' ? 'dd-status-badge-ok' : 'dd-status-badge-err'}`}>
              {apiStatus === 'connected' ? '✅ Connected' : '❌ Error'}
            </span>
          </div>
          <p className="dd-status-detail">
            {apiStatus === 'connected' ? 'Status: ok' : 'Cannot connect to API'}
          </p>
        </div>
      </div>

      <div className={`dd-status-card ${dbStatus === 'connected' ? 'dd-status-card-ok' : 'dd-status-card-err'}`}>
        <div className="dd-vstack dd-gap-2" style={{ alignItems: 'flex-start' }}>
          <div className="dd-hstack dd-gap-2">
            <span className="dd-text-semibold">Database:</span>
            <span className={`dd-status-badge ${dbStatus === 'connected' ? 'dd-status-badge-ok' : 'dd-status-badge-err'}`}>
              {dbStatus === 'connected' ? '✅ Connected' : '❌ Error'}
            </span>
          </div>
          <p className="dd-status-detail">
            {dbStatus === 'connected' ? 'Database connection successful!' : 'Cannot connect to database'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Home;
